import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { EdiError } from '../../../../models/api/view/claim/documentation/edi/edi-error';
import { EdiRecord } from '../../../../models/api/view/claim/documentation/edi/edi-record';
import { ExportService } from '../../../../services/export/export.service';
import { BaseComponent } from '../../../base/base.component';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-electronic-data-interchange',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    PageHeaderComponent,
    CommonModule,
  ],
  template: `
    <app-page-header
      label="EDI (Electronic Data Interchange)"
      labelCase="none"
    />
    <app-table
      #ediTable
      [dataSource]="ediRecords"
      [pageSize]="10"
      [scrollable]="false"
      [expandable]="true"
      [hideExpandAll]="true"
      [showExport]="true"
      dataKey="recNo"
      recordLabel="record"
      [stripedRows]="false"
      [error]="error"
      (rowExpand)="onRowExpand($event)"
      (export)="onExportEdi($event)"
    >
      <app-column name="partner" label="Partner" />
      <app-column name="st" label="ST" />
      <app-column name="doc" label="DOC" />
      <app-column name="mtc" label="MTC" />
      <app-column name="sent" label="Sent" format="d" />
      <app-column name="received" label="Received" format="d" />
      <app-column name="status" label="Status" />
      <app-column name="err" label="ERR" />
      <app-column name="sup" label="SUP" />
      <app-column name="source" label="Source" />
      <app-column name="generated" label="Generated" />
      <app-column name="agencyNumber" label="Agency Number" />
      <ng-template #expand let-data>
        <app-table
          #errors
          [dataSource]="errorsByRecNo[data.recNo] || []"
          [scrollable]="true"
          [expandable]="false"
          [showExport]="true"
          dataKey="element"
          recordLabel="error"
          [showPaginator]="false"
          [stripedRows]="true"
          [theme]="'Dark'"
          [loading]="loading"
          [error]="rowExpandError"
          (export)="onExportErrors($event, data.recNo)"
        >
          <app-column name="source" label="Source" />
          <app-column name="element" label="Element" />
          <app-column name="error" label="Error" />
          <app-column name="varSeg" label="VAR/SEG" />
          <app-column name="description" label="Description" />
        </app-table>
      </ng-template>
    </app-table>
  `,
  styles: ``,
})
export class ElectronicDataInterchangeComponent
  extends BaseComponent
  implements OnInit
{
  private datePipe = inject(DatePipe);
  private exportService = inject(ExportService);

  ediRecords: EdiRecord[] = [];
  errorsByRecNo: Record<number, EdiError[]> = {};
  loading = false;
  error: any = null;
  rowExpandError: any = null;

  @HandleLoadingState({ useViewLoading: true })
  async ngOnInit() {
    await this.loadEdiData();
  }

  @HandleLoadingState()
  async loadEdiData() {
    const claimKey = getClaimKey();
    const response = await this.api.edi.get(claimKey);
    this.ediRecords = response.ediRecords;
  }

  @HandleLoadingState({ errorProperty: 'rowExpandError' })
  async onRowExpand(event: any) {
    const recNo = event.data.recNo;
    const errCount = event.data.err;

    if (!this.errorsByRecNo[recNo]) {
      if (errCount && errCount > 0) {
        const claimKey = getClaimKey();
        const errors = await this.api.edi.getErrors(claimKey, recNo);
        this.errorsByRecNo[recNo] = errors;
      } else {
        this.errorsByRecNo[recNo] = [];
      }
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onExportEdi(format: ExportFormat) {
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `EDI_${formattedDate}.${format}`;

    const headers = [
      'Partner',
      'ST',
      'DOC',
      'MTC',
      'Sent',
      'Received',
      'Status',
      'ERR',
      'SUP',
      'Source',
      'Generated',
      'Agency Number',
    ];

    const rows = this.ediRecords.map((record) => [
      record.partner || '',
      record.st || '',
      record.doc || '',
      record.mtc || '',
      record.sent ? this.datePipe.transform(record.sent, 'MM/dd/yyyy') : '',
      record.received
        ? this.datePipe.transform(record.received, 'MM/dd/yyyy')
        : '',
      record.status || '',
      record.err?.toString() || '',
      record.sup || '',
      record.source || '',
      record.generated || '',
      record.agencyNumber || '',
    ]);

    if (format === 'csv') {
      await this.exportService.exportCsv(
        { fileName: filename, headers, rows },
        filename,
      );
    } else if (format === 'xlsx') {
      await this.exportService.exportExcel(
        {
          fileName: filename,
          sheets: [{ label: 'EDI', headers, rows }],
        },
        filename,
      );
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onExportErrors(format: ExportFormat, recNo: number) {
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `EDI_Errors_${formattedDate}.${format}`;

    const headers = ['Source', 'Element', 'Error', 'VAR/SEG', 'Description'];

    const errors = this.errorsByRecNo[recNo] || [];
    const rows = errors.map((error) => [
      error.source || '',
      error.element || '',
      error.error || '',
      error.varSeg || '',
      error.description || '',
    ]);

    if (format === 'csv') {
      await this.exportService.exportCsv(
        { fileName: filename, headers, rows },
        filename,
      );
    } else if (format === 'xlsx') {
      await this.exportService.exportExcel(
        {
          fileName: filename,
          sheets: [{ label: `EDI Errors`, headers, rows }],
        },
        filename,
      );
    }
  }
}
